// Medical / Lab Portal — Raise Ticket
//
// Internal CRM-style ticket form. Mirrors the visual language of
// RaiseQuery (same SectionCard, FieldLabel, fieldSx, MP color tokens)
// but the data shape is different — this is for branch operations
// issues (stock, logistics, equipment, billing, IT, HR, etc.) rather
// than report-specific QA queries.
//
// Auto-routing / SLA / escalation chain shown to admins is intentionally
// NOT rendered here. The portal user only fills the ticket; the admin
// triage UI handles routing visualisation.
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { MP, MONO, Alert, SectionCard, CardHeader, FieldLabel } from './shared';
import { BiIcon } from '../../../layouts/SidebarNav';
import { useAuth } from '../../../context/AuthContext';
import { raiseTicket } from '../../../services/ticketApi';
import { TICKET_CATEGORY_TREE } from './ticketCrmMock';

// Fixed routing — every ticket from this portal goes to CSD at the
// Hyderabad TS branch. The TrustTicket helpdesk treats both as
// top-level fields, so we pass them on every POST instead of asking
// staff to re-select them per ticket.
const FIXED_BRANCH     = 'Hyderabad TS';
const FIXED_DEPARTMENT = 'Customer Support';

// `onBack`     — fires when the user cancels OR returns to dashboard.
// `onCreated`  — called with the helpdesk response after a successful
//                submit. Parent (MedicalDashboard) uses this to swap
//                to the My Tickets view so staff land on their new
//                ticket immediately.
const RaiseTicket = ({ onBack, onCreated }) => {
  const { user } = useAuth();
  const raisedByName = user?.name || user?.email || 'Current user';
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    category: '',
    subCategory: '',
    specificIssue: '',
    subject: '',
    description: '',
  });
  const [toast, setToast] = useState({ open: false, msg: '', tone: 'success' });

  // Helper to update one form field at a time and reset dependent
  // dropdowns (subCategory depends on category, specificIssue depends
  // on both) so a stale child value can't slip through after a parent
  // change.
  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'category') { next.subCategory = ''; next.specificIssue = ''; }
      if (k === 'subCategory') { next.specificIssue = ''; }
      return next;
    });
  };

  // Cascade options for the three category dropdowns. Memoised so we
  // don't recompute on every keystroke; they only depend on the chosen
  // parent values.
  const subCategoryOptions = useMemo(() => {
    const cat = TICKET_CATEGORY_TREE.find((c) => c.label === form.category);
    return cat?.children || [];
  }, [form.category]);

  const specificIssueOptions = useMemo(() => {
    const sub = subCategoryOptions.find((s) => s.label === form.subCategory);
    return sub?.children || [];
  }, [subCategoryOptions, form.subCategory]);

  // Everything still outstanding, in field order. The form used to
  // validate only on submit and only one field at a time: you clicked
  // Submit, got a toast about the category, fixed it, clicked again, got a
  // toast about the sub-category. Four round trips to learn four things
  // the form already knew.
  const missing = [
    !form.category      ? 'Issue category' : null,
    !form.subCategory   ? 'Sub-category' : null,
    !form.specificIssue ? 'Specific issue' : null,
    !form.subject.trim()? 'Subject' : null,
  ].filter(Boolean);

  const onSubmit = async () => {
    if (missing.length) {
      return setToast({ open: true, msg: `Still needed: ${missing.join(', ')}`, tone: 'error' });
    }
    if (!user?.client_id) {
      return setToast({
        open: true,
        msg: 'Your account is missing a Client ID. Please contact support to link one.',
        tone: 'error',
      });
    }

    // Helpdesk only has top-level title + description. We fold the
    // routing trio (Category > Sub-Category > Specific Issue) into a
    // prefixed line so CSD still sees the precise selection on
    // every ticket, then keep the customer's free-form description
    // below. Branch + Department travel as native fields the
    // helpdesk understands.
    const categoryLine = `[${form.category} › ${form.subCategory} › ${form.specificIssue}]`;
    const description = [
      categoryLine,
      form.description.trim(),
    ].filter(Boolean).join('\n\n');

    setSubmitting(true);
    try {
      const res = await raiseTicket({
        customerId:    user.client_id,
        customerName:  user.name  || raisedByName,
        customerEmail: user.email || '',
        customerPhone: user.phone || '',
        title:         form.subject.trim(),
        description,
        branch:        FIXED_BRANCH,
        department:    FIXED_DEPARTMENT,
      });
      setToast({
        open: true,
        msg: res?.code
          ? `Ticket ${res.code} raised. Our team will respond within the agreed TAT.`
          : 'Ticket raised. Our team will respond within the agreed TAT.',
        tone: 'success',
      });
      // Hand off to the parent — it knows what surface to flip to.
      // We give it a beat so the success toast is visible before the
      // page transitions.
      setTimeout(() => {
        if (onCreated) onCreated(res);
        else onBack?.();
      }, 900);
    } catch (err) {
      const msg = err?.message || 'Submission failed. Please try again.';
      setToast({ open: true, msg, tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-fill subject with the specific-issue label the first time the
  // user picks one — saves a redundant retype but doesn't lock the field
  // (admin/CRM convention from the screenshot).
  useEffect(() => {
    if (form.specificIssue && !form.subject) {
      setForm((f) => ({ ...f, subject: form.specificIssue }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.specificIssue]);

  // The three dropdowns are one decision made in three parts, so they are
  // drawn as a path that fills in left to right rather than three unrelated
  // selects. Each one gates the next, which the old form only revealed by
  // disabling a control with no explanation of why.
  const trail = [form.category, form.subCategory, form.specificIssue];

  return (
    <Box sx={{ pb: 2 }}>
      <Alert tone="info" sx={{ mb: 2 }}>
        The ticket is auto-routed to the right department and assignee from the
        issue you pick. Critical-priority tickets are escalated immediately.
      </Alert>

      <SectionCard>
        <CardHeader
          title="Raise a Ticket"
          subtitle="Branch operations — stock, logistics, equipment, billing, IT, HR"
          right={(
            <Stack direction="row" spacing={0.75} alignItems="center">
              <RoutingChip label="Branch" value={FIXED_BRANCH} />
              <RoutingChip label="Dept" value={FIXED_DEPARTMENT} />
            </Stack>
          )}
        />

        <Box sx={{ p: '15px' }}>
          {/* The path so far. Reads as a sentence once filled, and shows at
              a glance how precisely the ticket has been classified. */}
          <Stack
            direction="row"
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{
              mb: 2, px: '11px', py: '9px',
              bgcolor: MP.bg,
              border: `1px solid ${MP.border}`,
              borderRadius: MP.radius,
            }}
          >
            <Typography sx={{
              fontFamily: MONO, fontSize: 10, letterSpacing: '.1em',
              textTransform: 'uppercase', color: MP.ink3, mr: 1.25,
            }}>
              Routing
            </Typography>
            {trail.map((step, n) => (
              <Stack key={n} direction="row" alignItems="center">
                {n > 0 && (
                  <Box sx={{ color: MP.ink3, mx: 0.75, display: 'flex' }}>
                    <BiIcon name="chevron-right" size={10} />
                  </Box>
                )}
                <Typography sx={{
                  fontSize: 12.5,
                  fontWeight: step ? 600 : 400,
                  color: step ? MP.dark : MP.ink3,
                }}>
                  {step || ['Category', 'Sub-category', 'Specific issue'][n]}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>Issue Category</FieldLabel>
              <TextField
                select fullWidth size="small"
                value={form.category}
                onChange={set('category')}
                SelectProps={{ displayEmpty: true }}
                sx={fieldSx}
              >
                <MenuItem value=""><Box component="span" sx={{ color: MP.ink3 }}>Select…</Box></MenuItem>
                {TICKET_CATEGORY_TREE.map((c) => (
                  <MenuItem key={c.label} value={c.label}>{c.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldLabel required>Sub-Category</FieldLabel>
              <TextField
                select fullWidth size="small"
                value={form.subCategory}
                onChange={set('subCategory')}
                disabled={!form.category}
                SelectProps={{ displayEmpty: true }}
                sx={fieldSx}
              >
                <MenuItem value="">
                  <Box component="span" sx={{ color: MP.ink3 }}>
                    {form.category ? 'Select…' : 'Pick a category first'}
                  </Box>
                </MenuItem>
                {subCategoryOptions.map((sc) => (
                  <MenuItem key={sc.label} value={sc.label}>{sc.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel required>Specific Issue</FieldLabel>
              <TextField
                select fullWidth size="small"
                value={form.specificIssue}
                onChange={set('specificIssue')}
                disabled={!form.subCategory}
                SelectProps={{ displayEmpty: true }}
                sx={fieldSx}
              >
                <MenuItem value="">
                  <Box component="span" sx={{ color: MP.ink3 }}>
                    {form.subCategory ? 'Select…' : 'Pick a sub-category first'}
                  </Box>
                </MenuItem>
                {specificIssueOptions.map((i) => (
                  <MenuItem key={i.label} value={i.label}>{i.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel required>Subject</FieldLabel>
              <TextField
                fullWidth size="small"
                value={form.subject}
                onChange={set('subject')}
                placeholder="One-line summary"
                sx={fieldSx}
                inputProps={{ maxLength: 120 }}
              />
              <Typography sx={{ fontSize: 11, color: MP.ink3, mt: 0.5 }}>
                Filled in from the specific issue — edit it to something the
                team will recognise in a list. {form.subject.length}/120
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel>Description</FieldLabel>
              <TextField
                fullWidth multiline minRows={6}
                value={form.description}
                onChange={set('description')}
                placeholder="Patient name, sample ID, time of incident, what was tried, what is expected…"
                sx={fieldSx}
              />
            </Grid>
          </Grid>

          {/* Raised by. One read-only line, not the whole card it used to
              occupy — it is context, not an input. */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${MP.borderSoft}` }}
          >
            <Box sx={{ color: MP.ink3, display: 'flex' }}>
              <BiIcon name="person-fill" size={13} />
            </Box>
            <Typography sx={{ fontSize: 12.5, color: MP.textMuted }}>
              Raised by <Box component="span" sx={{ color: MP.dark, fontWeight: 600 }}>{raisedByName}</Box>
              {user?.client_id ? ` · ${user.client_id}` : ''}
            </Typography>
          </Stack>
        </Box>
      </SectionCard>

      {/* Action bar. Sticky, and it names what is outstanding rather than
          leaving the user to discover it one toast at a time. */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          mt: 2,
          px: '15px',
          py: '11px',
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: MP.radius,
          boxShadow: '0 -2px 10px rgba(16,21,28,.06)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ color: missing.length ? MP.amber : MP.green, display: 'flex', flexShrink: 0 }}>
              <BiIcon name={missing.length ? 'exclamation-circle-fill' : 'check-circle-fill'} size={15} />
            </Box>
            <Typography sx={{ fontSize: 12.5, color: MP.textMuted, minWidth: 0 }}>
              {missing.length
                ? <>Still needed: <Box component="span" sx={{ color: MP.dark, fontWeight: 600 }}>{missing.join(', ')}</Box></>
                : 'Ready to submit.'}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.25} sx={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: 13,
                borderRadius: MP.radius, borderColor: MP.border, color: MP.textMuted,
                '&:hover': { borderColor: MP.ink3, color: MP.dark, bgcolor: MP.white },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={submitting || missing.length > 0}
              startIcon={submitting ? null : <BiIcon name="send-fill" size={13} />}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: 13,
                borderRadius: MP.radius, bgcolor: MP.teal, color: MP.white,
                '&:hover': { bgcolor: MP.tealDark },
                '&.Mui-disabled': { bgcolor: MP.borderSoft, color: MP.ink3 },
              }}
            >
              {submitting ? 'Submitting…' : 'Submit ticket'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          severity={toast.tone}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {toast.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

// ─── Atoms (kept local to mirror RaiseQuery's style) ───────────

// Small two-line chip used in the "Routes to" pinned strip so the
// staff member sees branch + department at a glance before they fill
// the rest of the form.
const RoutingChip = ({ label, value }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: '8px', py: '2px',
      bgcolor: 'rgba(255,255,255,.14)',
      borderRadius: '20px',
      whiteSpace: 'nowrap',
    }}
  >
    <Typography sx={{
      fontFamily: MONO, fontSize: 9.5, fontWeight: 600,
      color: 'rgba(255,255,255,.7)',
      letterSpacing: '.08em', textTransform: 'uppercase',
    }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 600, color: 'rgba(255,255,255,.95)' }}>
      {value}
    </Typography>
  </Box>
);


const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: MP.white,
    borderRadius: '6px',
    fontSize: 13,
    '& fieldset': { borderColor: MP.border, borderWidth: 1 },
    '&:hover fieldset': { borderColor: MP.ink3 },
    '&.Mui-focused fieldset': { borderColor: MP.teal, borderWidth: 1 },
    '&.Mui-focused': { boxShadow: MP.focus },
  },
};

export default RaiseTicket;
